import { BankEnum, SubBrandEnum } from "./types/enums";
/**
 * Util function used to extract the correct bank from the URL pathname.
 * Expects `window.location.pathname` to be on a specific format. Either
 *
 *  a) /bank_name/nettbank-\*, as used in digitalbank
 *
 *  b) /language_code/bank_name/\*, as used on open pages
 *
 * @returns
 * 'none' is returned if the path does not satisfy above conditions, or when we don't recognize the bank
 *
 * 'all' is returned if bank_name equals 'bank'
 *
 * @category Helper functions
 */
export function extractBankFromUri() {
    if (window.location.origin && window.location.origin.includes('kundeforsikring.sparebank1.no')) {
        return BankEnum.All;
    }
    return getBankFromPath(window.location.pathname);
}
/**
 * Util function used to extract the SubBrand from the URL pathname.
 * Uses the same logic as {@link extractBankFromUri}. See related documentation on how the URI should be formatted.
 *
 * @category Helper functions
 */
export function extractBankSubBrandFromUri() {
    var bank = getBankFromPath(window.location.pathname);
    switch (bank) {
        case 'none':
            console.warn('Failed to extract SubBrand from URI, it is possibly malformed. This will impact routing in Adobe.');
            return 'none';
        case 'all':
            return 'bank';
        default:
            return bank;
    }
}
/**
 * Util function to find subBrand based on FID (Financial Institution ID).
 *
 * @category Helper functions
 * @param fid
 */
export function fidToSubBrand(fid) {
    switch (fid) {
        case 'fid-ostfold-akershus':
            return SubBrandEnum.Ostfold;
        case 'fid-ostlandet':
            return SubBrandEnum.Ostlandet;
        case 'fid-lom-skjaak':
            return SubBrandEnum.LomSkjaak;
        case 'fid-gudbrandsdal':
            return SubBrandEnum.Gudbrandsdal;
        case 'fid-ringerike-hadeland':
            return SubBrandEnum.RingerikeHadeland;
        case 'fid-hallingdal-valdres':
            return SubBrandEnum.HallingdalValdres;
        case 'fid-sr-bank':
            return SubBrandEnum.SrBank;
        case 'fid-nordmore':
            return SubBrandEnum.Nordmore;
        case 'fid-smn':
            return SubBrandEnum.Smn;
        case 'fid-nord-norge':
            return SubBrandEnum.NordNorge;
        case 'fid-helgeland':
            return SubBrandEnum.Helgeland;
        case 'fid-sorost':
            return SubBrandEnum.Sorost;
        // TODO: Da dette ble skrevet eksisterte ikke fid-sognogfjordane enda. FID-en blir ganske sikkert fid-sognogfjordane, men bør dobbeltsjekkes når sognogfjordane er oppe og kjøre (antageligvis mot slutten av 2024)
        case 'fid-sognogfjordane':
            return SubBrandEnum.SognOgFjordane;
        default:
            return SubBrandEnum.None;
    }
}
function getBankFromPath(pathname) {
    var _a;
    if (!pathname || pathname === '/') {
        return 'none';
    }
    var bankSemanticUrlName = (_a = pathname
        .split('/')
        .filter(function (val) { return val != 'nb' && val != 'en' && val != 'nn'; })[1]) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    if (bankSemanticUrlName == '') {
        return 'none';
    }
    if (bankSemanticUrlName == 'bank') {
        return 'all';
    }
    return convertToBank(bankSemanticUrlName);
}
function convertToBank(bankSemanticUrlName) {
    switch (bankSemanticUrlName) {
        case 'hallingdal-valdres':
            return 'hallingdal';
        case 'sore-sunnmore':
            return 'smn';
        case 'ostfold-akershus':
        case 'hallingdal':
        case 'ostlandet':
        case 'lom-skjaak':
        case 'gudbrandsdal':
        case 'ringerike-hadeland':
        case 'sr-bank':
        case 'nordmore':
        case 'smn':
        case 'nord-norge':
        case 'helgeland':
        case 'sorost':
        case 'sognogfjordane':
            return bankSemanticUrlName;
        default:
            return 'none';
    }
}
//# sourceMappingURL=bankUtil.js.map